import {
  AfterViewInit,
  AfterViewChecked,
  Component,
  ElementRef,
  forwardRef,
  Input,
  Renderer2,
  ViewChild,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractFormComponent } from '../_abstract-classes/abstract-form-component';

@Component({
  selector: 'base-textarea',
  templateUrl: './base-textarea.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BaseTextareaComponent),
      multi: true,
    },
  ],
})
export class BaseTextareaComponent
  extends AbstractFormComponent
  implements ControlValueAccessor, AfterViewInit, AfterViewChecked, OnChanges
{
  @ViewChild('textarea') element: ElementRef;

  @Input() isDisabled = false;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() name = '';
  @Input() isInvalid = false;
  @Input() isPreview = false;
  @Input() errorMessage: string | null = null;
  @Input() tabindex = 0;
  @Input() isRequired = false;
  @Input() rows = 1;
  @Input() previewClass: string;
  @Input() customClasses = '';
  @Input() autoGrowEnabled = false;

  private autoSizeMaxRows = 6;
  private _value = '';
  private lastHeight: number = -1;

  constructor(protected renderer: Renderer2, private cdr: ChangeDetectorRef) {
    super(renderer);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isPreview?.previousValue && !changes.isPreview.currentValue) {
      this.adjustTextareaHeight();
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.adjustTextareaHeight();
  }

  ngAfterViewChecked(): void {
    if (this.autoGrowEnabled && this.element?.nativeElement) {
      const textarea = this.element.nativeElement;
      const currentHeight = textarea.scrollHeight;

      if (currentHeight !== this.lastHeight) {
        this.adjustTextareaHeight();
        this.lastHeight = currentHeight;
      }
    }
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: string): void {
    this._value = value ?? '';
    this.cdr.markForCheck();
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  onTextareaInput(event: Event): void {
    this._value = (event.target as HTMLTextAreaElement).value;
    this.onChange(this._value);
    this.onTouched();
  }

  get value(): string {
    return this._value;
  }

  set value(value: string) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
    this.adjustTextareaHeight();
  }

  private adjustTextareaHeight(): void {
    if (!this.autoGrowEnabled || !this.element?.nativeElement) {
      return;
    }

    const textarea = this.element.nativeElement;
    this.renderer.setStyle(textarea, 'height', 'auto');

    if (!this._value || this._value.trim() === '') {
      return;
    }

    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 10);
    const rows = Math.floor(textarea.scrollHeight / lineHeight);

    if (rows <= this.autoSizeMaxRows) {
      this.renderer.setStyle(textarea, 'height', `${textarea.scrollHeight}px`);
      this.renderer.setStyle(textarea, 'overflow-y', 'hidden');
    } else {
      this.renderer.setStyle(textarea, 'height', `${lineHeight * this.autoSizeMaxRows}px`);
      this.renderer.setStyle(textarea, 'overflow-y', 'scroll');
    }
  }

  focus() {
    this.element.nativeElement.focus();
  }
}
